import { CompanyLegalBeneficiaryOnboardingStatus } from '__generated__/GQL';

import { VerificationModuleDataQuery } from '../../../graphql/queries/verificationModuleData.gql';
import { VerificationStatus } from '../../../lib/steps/types';

interface TriageLegalBeneficiariesProps {
  legalBeneficiaries: VerificationModuleDataQuery['viewer']['company']['legalBeneficiaries'];
}

/**
 * Group legal beneficiaries by their status, not counting the account holder.
 */
const triageLegalBeneficiaries = ({
  legalBeneficiaries,
}: TriageLegalBeneficiariesProps) => {
  if (!legalBeneficiaries) {
    return null;
  }

  const otherLegalBeneficiaries = legalBeneficiaries.filter(
    ({ isCurrentUser }) => isCurrentUser === false,
  );

  if (otherLegalBeneficiaries.length === 0) {
    return null;
  }

  const count = otherLegalBeneficiaries.reduce(
    (counts, { onboardingStatus }) => ({
      pending:
        onboardingStatus === CompanyLegalBeneficiaryOnboardingStatus.InReview
          ? counts.pending + 1
          : counts.pending,
      rejected: [
        CompanyLegalBeneficiaryOnboardingStatus.Refused,
        CompanyLegalBeneficiaryOnboardingStatus.Invalid,
      ].includes(onboardingStatus)
        ? counts.rejected + 1
        : counts.rejected,
      total: counts.total + 1,
      valid:
        onboardingStatus === CompanyLegalBeneficiaryOnboardingStatus.Validated
          ? counts.valid + 1
          : counts.valid,
    }),
    { pending: 0, rejected: 0, total: 0, valid: 0 },
  );

  let status = null;

  if (count.total === count.valid) {
    status = VerificationStatus.Validated;
  } else if (count.total === count.valid + count.pending) {
    status = VerificationStatus.Completed;
  } else if (count.rejected > 0) {
    status = VerificationStatus.Invalid;
  } else {
    status = VerificationStatus.ToDo;
  }

  const isMissingSomeEmail = otherLegalBeneficiaries.some(
    // If the account holder has been swapped with a legal beneficiary (via the BO),
    // then we can have a legal beneficiary without an invitation but with a companyUser
    ({ companyUser, invitation }) => !invitation?.inviteeEmail && !companyUser,
  );

  return {
    count,
    isMissingSomeEmail,
    status,
  };
};

export default triageLegalBeneficiaries;
